import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { LoaderCircle, MessageSquare, Trash2 } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import { SearchInput } from '@/_shared/components/SearchInput';
import { useWorkspace } from '@/_shared/contexts/WorkspaceContext';
import { useOrpc } from '@/_shared/hooks/useOrpc';
import { usePageTitle } from '@/_shared/hooks/usePageTitle';

import { BUCKET_LABELS, BUCKET_ORDER, groupByTemporalBucket, type TemporalBucket } from '../lib/dates';

function formatOlderDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function ChatList(): React.ReactElement {
  usePageTitle('Chats');
  const [search, setSearch] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { wp } = useWorkspace();
  const orpc = useOrpc();

  const { data, isLoading } = useQuery(orpc.chat.list.queryOptions({ input: {} }));

  const allSessions = useMemo(() => data?.sessions ?? [], [data?.sessions]);

  const filteredSessions = useMemo(() => {
    if (!search.trim()) return allSessions;
    const q = search.toLowerCase();
    return allSessions.filter((s) => (s.title ?? 'New conversation').toLowerCase().includes(q));
  }, [allSessions, search]);

  const grouped = useMemo(() => groupByTemporalBucket(filteredSessions), [filteredSessions]);

  const deleteMutation = useMutation(
    orpc.chat.delete.mutationOptions({
      onSuccess: () => {
        void queryClient.invalidateQueries({
          queryKey: orpc.chat.list.queryOptions({ input: {} }).queryKey,
        });
      },
    }),
  );

  const handleDelete = useCallback(
    (e: React.MouseEvent, sessionId: string) => {
      e.preventDefault();
      e.stopPropagation();
      deleteMutation.mutate({ id: sessionId });
      if (location.pathname === wp(`/chat/${sessionId}`)) {
        void navigate(wp());
      }
    },
    [deleteMutation, location.pathname, navigate, wp],
  );

  const isSearching = search.trim().length > 0;

  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      {/* Header */}
      <div className="border-b px-6 py-4">
        <div className="flex items-center justify-between gap-4">
          <h1 className="text-lg font-semibold text-foreground">Chats</h1>
          <div className="flex items-center gap-3">
            <SearchInput value={search} onChange={setSearch} placeholder="Search chats..." className="w-56" />
          </div>
        </div>
      </div>

      {/* List */}
      <div className="flex-1 overflow-y-auto">
        {isLoading && (
          <div className="flex items-center justify-center py-16">
            <LoaderCircle size={24} className="animate-spin text-muted-foreground" />
          </div>
        )}

        {!isLoading && allSessions.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <MessageSquare size={48} className="mb-4 text-muted-foreground/30" />
            <h2 className="text-base font-medium text-foreground">No conversations yet</h2>
            <p className="mt-1 max-w-sm text-sm text-muted-foreground">Start a new chat to begin.</p>
          </div>
        )}

        {!isLoading && allSessions.length > 0 && filteredSessions.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <p className="text-sm text-muted-foreground">No chats matching &lsquo;{search}&rsquo;</p>
            <button onClick={() => setSearch('')} className="mt-2 text-sm text-brand hover:underline">
              Clear search
            </button>
          </div>
        )}

        {!isLoading && filteredSessions.length > 0 && (
          <div className="mx-auto max-w-3xl px-6 py-2">
            {isSearching
              ? // Flat list when searching
                filteredSessions.map((session) => (
                  <ChatRow key={session.id} session={session} onDelete={handleDelete} showDate />
                ))
              : // Temporal buckets when not searching
                BUCKET_ORDER.map((bucket) => {
                  const items = grouped[bucket];
                  if (items.length === 0) return null;
                  return <BucketSection key={bucket} bucket={bucket} sessions={items} onDelete={handleDelete} />;
                })}
          </div>
        )}
      </div>
    </div>
  );
}

function BucketSection({
  bucket,
  sessions,
  onDelete,
}: {
  bucket: TemporalBucket;
  sessions: { id: string; title: string | null; createdAt: string }[];
  onDelete: (e: React.MouseEvent, id: string) => void;
}): React.ReactElement {
  return (
    <div className="mb-2">
      <div className="sticky top-0 z-10 bg-background px-1 py-2">
        <span className="text-xs font-medium tracking-wider text-muted-foreground/60 uppercase">
          {BUCKET_LABELS[bucket]}
        </span>
      </div>
      {sessions.map((session) => (
        <ChatRow key={session.id} session={session} onDelete={onDelete} showDate={bucket === 'older'} />
      ))}
    </div>
  );
}

function ChatRow({
  session,
  onDelete,
  showDate,
}: {
  session: { id: string; title: string | null; createdAt: string };
  onDelete: (e: React.MouseEvent, id: string) => void;
  showDate?: boolean;
}): React.ReactElement {
  const { wp } = useWorkspace();
  const title = session.title ?? 'New conversation';
  return (
    <Link
      to={wp(`/chat/${session.id}`)}
      className="group flex items-center gap-3 rounded-md px-2 py-2.5 transition-colors hover:bg-muted/50"
    >
      <MessageSquare size={14} className="shrink-0 text-muted-foreground" />
      <span className="min-w-0 flex-1 truncate text-sm text-foreground">{title}</span>
      {showDate && <span className="shrink-0 text-xs text-muted-foreground">{formatOlderDate(session.createdAt)}</span>}
      <button
        onClick={(e) => onDelete(e, session.id)}
        className="shrink-0 rounded p-1 text-muted-foreground/0 transition-colors group-hover:text-muted-foreground hover:text-red-600"
        title="Delete chat"
        aria-label="Delete chat"
      >
        <Trash2 size={14} />
      </button>
    </Link>
  );
}
